import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Toast,
  ToastContainer,
  Table,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import axios from "axios";

import Swal from "sweetalert2";
import API_BASE_URL from "../config";

const LeaveView = ({ employeeId }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [isApprover, setIsApprover] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [form, setForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [allocationForm, setAllocationForm] = useState({});
  const [editingAllocation, setEditingAllocation] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [modalAction, setModalAction] = useState(null); // { requestId, status }
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  useEffect(() => {
    fetchCompanyId();
  }, [employeeId]);

  const fetchCompanyId = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/employees/${employeeId}`);
      setCompanyId(res.data.company_id);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load company info",
      });
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchLeaveTypes();
      fetchBalances();
      fetchRequests();
      fetchPendingApprovals();
    }
  }, [companyId]);

  const fetchLeaveTypes = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/leave_types`
      );
      setLeaveTypes(res.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load leave types",
      });
    }
  };

  const fetchBalances = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/leave_balance`
      );
      setBalances(res.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load leave balances",
      });
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/leave_requests`
      );
      setRequests(res.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load leave requests",
      });
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/pending_leave_approvals`
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setPendingApprovals(data);
      setIsApprover(data.length > 0);
    } catch (err) {
      console.error("Failed to fetch pending approvals:", err);
      setPendingApprovals([]);
      setIsApprover(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_BASE_URL}/employees/${employeeId}/leave_requests`,
        form
      );
      Swal.fire({ icon: "success", title: "Leave Requested", timer: 1500 });
      fetchRequests();
      fetchBalances();
      setForm({ leave_type_id: "", start_date: "", end_date: "", reason: "" });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to submit leave request",
      });
    }
  };

 const handleApprove = (requestId, status) => {
  setModalAction({ requestId, status });
  setComment('');
  setShowCommentModal(true);
};

const handleModalSubmit = async () => {
  if (!modalAction) return;

  setLoading(true);
  try {
    await axios.post(`${API_BASE_URL}/leave_requests/${modalAction.requestId}/approve`, {
      status: modalAction.status,
      comments: comment.trim() || null,
    });

    showToast(`${modalAction.status === 'approved' ? 'Approved' : 'Rejected'} successfully`, 'success');
    
    // Refresh data
    fetchPendingApprovals();
    fetchRequests();
    fetchBalances();
  } catch (err) {
    showToast('Failed to update leave request', 'danger');
  } finally {
    setLoading(false);
    setShowCommentModal(false);
  }
};

const showToast = (message, variant = 'success') => {
  setToast({ show: true, message, variant });
  setTimeout(() => setToast({ show: false, message: '', variant: 'success' }), 3000);
};

  const handleAllocationChange = (leaveTypeId, value) => {
    setAllocationForm((prev) => ({ ...prev, [leaveTypeId]: value }));
  };

  const handleSaveAllocation = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/employees/${employeeId}/leave_allocation`,
        allocationForm
      );
      Swal.fire({ icon: "success", title: "Allocation saved", timer: 1500 });
      setEditingAllocation(false);
      fetchBalances();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save allocation",
      });
    }
  };

  return (
    <div>
      <h4>Leave Management</h4>

      {/* BALANCES + ALLOCATION */}
      <h5>Leave Balances</h5>
      {editingAllocation ? (
        <Table striped hover>
          <thead>
            <tr>
              <th>Type</th>
              <th>Current</th>
              <th>Set New</th>
            </tr>
          </thead>
          <tbody>
            {balances.map((bal) => (
              <tr key={bal.id}>
                <td>{bal.name}</td>
                <td>{bal.balance}</td>
                <td>
                  <Form.Control
                    type="number"
                    min="0"
                    defaultValue={bal.balance}
                    onChange={(e) =>
                      handleAllocationChange(bal.leave_type_id, e.target.value)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Table striped hover>
          <thead>
            <tr>
              <th>Type</th>
              <th>Balance</th>
              <th>Used</th>
            </tr>
          </thead>
          <tbody>
            {balances.map((bal) => (
              <tr key={bal.id}>
                <td>{bal.name}</td>
                <td>{bal.balance}</td>
                <td>{bal.used}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <div className="mt-2">
        {editingAllocation ? (
          <>
            <Button variant="success" size="sm" onClick={handleSaveAllocation}>
              Save
            </Button>{" "}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditingAllocation(false)}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setEditingAllocation(true)}
          >
            Set Leave Allocation
          </Button>
        )}
      </div>

      {/* APPLY LEAVE */}
      <h5 className="mt-4">Apply for Leave</h5>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={4}>
            <Form.Label>Leave Type</Form.Label>
            <Form.Select
              name="leave_type_id"
              value={form.leave_type_id}
              onChange={handleChange}
              required
            >
              <option>Select</option>
              {leaveTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              name="start_date"
              value={form.start_date}
              onChange={handleChange}
              required
            />
          </Col>
          <Col md={4}>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              name="end_date"
              value={form.end_date}
              onChange={handleChange}
              required
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              name="reason"
              value={form.reason}
              onChange={handleChange}
            />
          </Col>
        </Row>
        <Button type="submit" className="mt-3">
          Apply
        </Button>
      </Form>

      {/* PENDING APPROVALS */}
      {isApprover && (
        <>
          <h5 className="mt-4">Pending Leave Approvals</h5>
          <Table striped hover>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Type</th>
                <th>Start</th>
                <th>End</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(pendingApprovals) ? pendingApprovals : []).map(
                (req) => (
                  <tr key={req.id}>
                    <td>{req.employee_name}</td>
                    <td>{req.leave_type}</td>
                    <td>{req.start_date}</td>
                    <td>{req.end_date}</td>
                    <td>{req.days}</td>
                    <td>{req.reason || "—"}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleApprove(req.id, "approved")}
                        className="me-1"
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleApprove(req.id, "rejected")}
                      >
                        Reject
                      </Button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </>
      )}

      {/* REQUESTS */}
      <h5 className="mt-4">Leave Requests</h5>
      <Table striped hover>
        <thead>
          <tr>
            <th>Type</th>
            <th>Start</th>
            <th>End</th>
            <th>Days</th>
            <th>Status</th>
            <th>Approvals</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req) => (
            <tr key={req.id}>
              <td>{req.leave_type}</td>
              <td>{req.start_date}</td>
              <td>{req.end_date}</td>
              <td>{req.days}</td>
              <td>{req.status}</td>
              <td>
                {req.approvals
                  .map((a) => `${a.approver_name}: ${a.status}`)
                  .join(", ")}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* CALENDAR */}
      <h5>Leave Calendar</h5>
      <Table striped hover>
        <thead>
          <tr>
            <th>Date</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {requests
            .filter((r) => r.status === "approved")
            .flatMap((r) => {
              const dates = [];
              let d = new Date(r.start_date);
              const end = new Date(r.end_date);
              while (d <= end) {
                dates.push({
                  date: d.toISOString().split("T")[0],
                  status: `On Leave (${r.leave_type})`,
                });
                d.setDate(d.getDate() + 1);
              }
              return dates;
            })
            .map((c, i) => (
              <tr key={i}>
                <td>{c.date}</td>
                <td>{c.status}</td>
              </tr>
            ))}
        </tbody>
      </Table>

      {/* Comment Modal */}
<Modal show={showCommentModal} onHide={() => setShowCommentModal(false)} centered>
  <Modal.Header closeButton>
    <Modal.Title>
      {modalAction?.status === 'approved' ? 'Approve' : 'Reject'} Leave Request
    </Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <Form.Group>
      <Form.Label>Comments (optional)</Form.Label>
      <Form.Control
        as="textarea"
        rows={3}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Add any notes..."
        disabled={loading}
      />
    </Form.Group>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowCommentModal(false)} disabled={loading}>
      Cancel
    </Button>
    <Button
      variant={modalAction?.status === 'approved' ? 'success' : 'danger'}
      onClick={handleModalSubmit}
      disabled={loading}
    >
      {loading ? (
        <>
          <Spinner animation="border" size="sm" /> Processing...
        </>
      ) : (
        modalAction?.status === 'approved' ? 'Approve' : 'Reject'
      )}
    </Button>
  </Modal.Footer>
</Modal>

{/* Toast Notification */}
<ToastContainer position="top-end" className="p-3">
  <Toast
    show={toast.show}
    onClose={() => setToast({ ...toast, show: false })}
    bg={toast.variant}
    delay={3000}
    autohide
  >
    <Toast.Body className="text-white">{toast.message}</Toast.Body>
  </Toast>
</ToastContainer>
    </div>
    
  );
};

export default LeaveView;
